import { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { toast } from "sonner";
import {
    Mail,
    Lock,
    User,
    Phone,
    CalendarClock,
    Hammer,
    Home,
    Store,
    ArrowRight,
    Loader2,
    Eye,
    EyeOff,
    Car,
    ShieldCheck,
    CheckCircle2,
    XCircle,
    Clock3,
    Gift,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AddressAutocomplete from "./AddressAutocomplete";
import { useSiteConfig } from "../context/SiteConfigContext";
import { speak, isVoiceAssistOn } from "../lib/speech";
import { ForceLogo } from "./ForceLogo";

export default function AuthWindow() {
    const { login } = useAuth();
    const [tab, setTab] = useState("signin");
    const [loading, setLoading] = useState(false);
    // Guided sign-up voiceover (Easy Mode + voice assist): the robot announces
    // the form and each field speaks its own instructions as it's focused.
    const { layout } = useSiteConfig();
    const guidedRef = useRef(false);
    const guideSpeak = (t) => { if (layout === "easy" && isVoiceAssistOn()) speak(t); };
    useEffect(() => {
        if (layout === "easy" && tab === "signup" && isVoiceAssistOn() && !guidedRef.current) {
            guidedRef.current = true;
            speak("Let's make your account together — I'll talk you through every box. First, type your full name.");
        }
        if (tab !== "signup") guidedRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, layout]);
    const [showPw, setShowPw] = useState(false);

    const [identifier, setIdentifier] = useState("");
    const [password, setPassword] = useState("");

    const [role, setRole] = useState("worker");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [address, setAddress] = useState("");
    const [phone, setPhone] = useState("");
    const [birthday, setBirthday] = useState("");
    const [regPw, setRegPw] = useState("");
    // Worker-only: licence + vehicle insurance for driving-job eligibility.
    const [hasDriving, setHasDriving] = useState(false);
    const [licenseNumber, setLicenseNumber] = useState("");
    const [insurancePolicy, setInsurancePolicy] = useState("");
    // Referral code — auto-filled when the visitor arrived via a ?ref=CODE link.
    const [refCode, setRefCode] = useState(() => {
        const fromUrl = new URLSearchParams(window.location.search).get("ref");
        if (fromUrl) {
            localStorage.setItem("force_ref", fromUrl.toUpperCase());
            return fromUrl.toUpperCase();
        }
        return localStorage.getItem("force_ref") || "";
    });
    // Instant verification overlay: { token, user, status, message }
    // status: "validating" | "passed" | "failed" | "pending"
    const [verify, setVerify] = useState(null);

    const [googleCfg, setGoogleCfg] = useState(null);
    const [rolePick, setRolePick] = useState(null); // { token, user } for a first-time Google user
    const gbtnRef = useRef(null);
    const [gbtnWidth, setGbtnWidth] = useState(0);

    useEffect(() => {
        api.get("/config")
            .then(({ data }) => { if (data.google_enabled && data.google_client_id) setGoogleCfg(data); })
            .catch(() => {});
    }, []);

    // Measure the available width BEFORE the button mounts (useLayoutEffect runs
    // pre-paint) so the Google iframe is created once at the correct pixel width.
    // We pass this width straight to GoogleLogin instead of CSS-scaling it — the
    // old `transform: scale()` wrapper caused Google's GSI iframe to mis-render
    // as a flashing white box (transforms break the iframe's internal layout).
    // A generous jitter guard keeps a resize from ever re-mounting the iframe.
    useLayoutEffect(() => {
        if (!googleCfg) return;
        const measure = () => {
            const w = gbtnRef.current?.clientWidth;
            if (!w) return;
            setGbtnWidth((prev) => (Math.abs(prev - Math.floor(w)) > 8 ? Math.floor(w) : prev));
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [googleCfg]);

    // Render the Google button ONCE, only after the width is known, at a real
    // pixel width that fits the card (no transforms, no re-mounts → no flicker).
    const googleButton = useMemo(() => {
        if (!googleCfg || !gbtnWidth) return null;
        return (
            <GoogleOAuthProvider clientId={googleCfg.google_client_id}>
                <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => toast.error("Google sign-in failed.")}
                    theme="filled_black"
                    shape="pill"
                    text="continue_with"
                    width={String(Math.min(400, gbtnWidth))}
                />
            </GoogleOAuthProvider>
        );
    }, [googleCfg?.google_client_id, gbtnWidth]);

    // Let other landing sections (e.g. the pro spotlight "Invite" CTA) open the
    // Create-Account tab, optionally preselecting a role.
    useEffect(() => {
        const start = (e) => {
            setTab("signup");
            const r = e.detail?.role;
            if (r === "homeowner" || r === "worker" || r === "business") setRole(r);
        };
        window.addEventListener("force:start-signup", start);
        return () => window.removeEventListener("force:start-signup", start);
    }, []);

    async function handleGoogleSuccess(cred) {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        setLoading(true);
        try {
            const { data } = await api.post("/auth/google", { credential: cred.credential });
            if (data.is_new) {
                // Keep the token in memory only (not localStorage) until they pick a role,
                // so abandoning the modal doesn't silently sign them in with the default role.
                setRolePick({ token: data.token, user: data.user });
            } else {
                login(data.token, data.user);
                toast.success(`Welcome, ${data.user.name.split(" ")[0]}!`);
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Google sign-in failed.");
        } finally {
            setLoading(false);
        }
    }

    async function chooseRole(role) {
        if (!rolePick) return;
        setLoading(true);
        try {
            let finalUser = rolePick.user;
            if (role !== rolePick.user.role) {
                const { data } = await api.post("/auth/role", { role }, { headers: { Authorization: `Bearer ${rolePick.token}` } });
                finalUser = data.user;
            }
            login(rolePick.token, finalUser);
            toast.success(`Welcome to FORCE, ${finalUser.name.split(" ")[0]}!`);
            setRolePick(null);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not set your account type.");
        } finally {
            setLoading(false);
        }
    }

    async function handleSignIn(e) {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/auth/login", { identifier, password });
            login(data.token, data.user);
            toast.success(`Welcome back, ${data.user.name.split(" ")[0]}!`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    }

    async function handleRegister(e) {
        e.preventDefault();
        const wantsDriving = role === "worker" && hasDriving;
        if (wantsDriving && (!licenseNumber.trim() || !insurancePolicy.trim())) {
            toast.error("Please enter both your driver's licence number and insurance policy number.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/auth/register", {
                name,
                email,
                address,
                phone,
                birthday,
                password: regPw,
                role,
                ...(wantsDriving ? { license_number: licenseNumber.trim(), insurance_policy: insurancePolicy.trim() } : {}),
                ...(refCode.trim() ? { referral_code: refCode.trim() } : {}),
            });
            if (wantsDriving) {
                // Hold the sign-in until the licence check finishes so the
                // worker sees the result right away (validating → pass/fail).
                setVerify({ token: data.token, user: data.user, status: "validating", message: "" });
                guideSpeak("One moment — I'm checking your driver's licence right now.");
                try {
                    // Keep the "Validating…" state visible for a beat even when
                    // the check answers instantly — feels deliberate, not glitchy.
                    const [vres] = await Promise.all([
                        api.post("/driving/verify", {
                            license_number: licenseNumber.trim(),
                            insurance_policy: insurancePolicy.trim(),
                        }, { headers: { Authorization: `Bearer ${data.token}` }, timeout: 30000 }),
                        new Promise((r) => setTimeout(r, 1400)),
                    ]);
                    const v = vres.data;
                    setVerify({ token: data.token, user: data.user, status: v.status, message: v.message });
                    guideSpeak(v.message);
                } catch {
                    const msg = "Instant verification is unavailable right now — our team will review it manually and notify you.";
                    setVerify({ token: data.token, user: data.user, status: "pending", message: msg });
                    guideSpeak(msg);
                }
            } else {
                login(data.token, data.user);
                toast.success("Account created. You're all set!");
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    }

    function finishVerification() {
        if (!verify) return;
        login(verify.token, { ...verify.user, driving_status: verify.status === "validating" ? "pending" : verify.status });
        setVerify(null);
        toast.success("Account created. You're all set!");
    }

    return (
        <motion.div
            data-testid="auth-window"
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="glass w-full max-w-md p-7 sm:p-8"
        >
            <div className="absolute top-0 left-0 right-0 h-px shimmer" />

            {/* Prominent brand logo */}
            <div className="flex flex-col items-center mb-6">
                <ForceLogo size={58} layout="vertical" />
                <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.25em] text-orange-400/90">
                    Let the FORCE assist you.
                </p>
            </div>

            {/* Tabs */}
            <div className="flex p-1 rounded-2xl bg-zinc-900/70 border border-white/5 mb-7">
                {[
                    { id: "signin", label: "Sign In" },
                    { id: "signup", label: "Create Account" },
                ].map((t) => (
                    <button
                        key={t.id}
                        data-testid={`tab-${t.id}`}
                        onClick={() => setTab(t.id)}
                        className="relative flex-1 py-2.5 text-sm font-semibold rounded-xl transition-colors"
                    >
                        {tab === t.id && (
                            <motion.div
                                layoutId="tab-pill"
                                className="absolute inset-0 rounded-xl bg-gradient-to-r from-orange-500/25 to-amber-500/20 border border-orange-500/50 shadow-[0_0_15px_rgba(255,122,0,0.2)]"
                                transition={{ type: "spring", stiffness: 380, damping: 30 }}
                            />
                        )}
                        <span className={`relative z-10 ${tab === t.id ? "text-white" : "text-zinc-400"}`}>
                            {t.label}
                        </span>
                    </button>
                ))}
            </div>

            <AnimatePresence mode="wait">
                {tab === "signin" ? (
                    <motion.form
                        key="signin"
                        onSubmit={handleSignIn}
                        initial={{ opacity: 0, x: -16 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 16 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-4"
                    >
                        <Field icon={User} testid="signin-identifier" placeholder="Email or username" value={identifier} onChange={setIdentifier} />
                        <PasswordField testid="signin-password" value={password} onChange={setPassword} show={showPw} setShow={setShowPw} />
                        <SubmitButton testid="signin-submit" loading={loading} label="Sign In" />
                    </motion.form>
                ) : (
                    <motion.form
                        key="signup"
                        onSubmit={handleRegister}
                        initial={{ opacity: 0, x: 16 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -16 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-4"
                    >
                        <div className="grid grid-cols-3 gap-2.5">
                            <RoleCard testid="role-worker" active={role === "worker"} onClick={() => setRole("worker")} icon={Hammer} title="I want work" sub="Find local gigs" />
                            <RoleCard testid="role-homeowner" active={role === "homeowner"} onClick={() => setRole("homeowner")} icon={Home} title="I need help" sub="Hire a pro" />
                            <RoleCard testid="role-business" active={role === "business"} onClick={() => setRole("business")} icon={Store} title="I'm a business" sub="Advertise here" />
                        </div>

                        {/* Guided sign-up voiceover: each field speaks its instructions on focus
                            (Easy Mode + voice assist only). */}
                        <div onFocusCapture={() => guideSpeak("First — type your full name.")}>
                            <Field icon={User} testid="reg-name" placeholder="Full name" value={name} onChange={setName} />
                        </div>
                        <div onFocusCapture={() => guideSpeak("Now, type your email address.")}>
                            <Field icon={Mail} testid="reg-email" type="email" placeholder="Email address" value={email} onChange={setEmail} />
                        </div>
                        <div onFocusCapture={() => guideSpeak("Your home address — start typing and tap it in the list.")}>
                            <AddressAutocomplete value={address} onChange={setAddress} />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" onFocusCapture={() => guideSpeak("Your phone number, and your birthday.")}>
                            <Field icon={Phone} testid="reg-phone" placeholder="Phone" value={phone} onChange={setPhone} />
                            <BirthdayField testid="reg-birthday" value={birthday} onChange={setBirthday} age={computeAge(birthday)} />
                        </div>

                        {/* Worker-only: licence + vehicle insurance for driving jobs */}
                        {role === "worker" && (
                            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3.5" data-testid="reg-driving-section">
                                <label className="flex cursor-pointer items-center gap-3">
                                    <input
                                        type="checkbox"
                                        data-testid="reg-driving-toggle"
                                        checked={hasDriving}
                                        onChange={(e) => setHasDriving(e.target.checked)}
                                        className="h-5 w-5 shrink-0 accent-orange-500"
                                    />
                                    <span className="flex items-center gap-2 text-sm font-semibold text-zinc-200">
                                        <Car className="h-4 w-4 text-orange-400" />
                                        I have a driver's licence &amp; vehicle insurance
                                    </span>
                                </label>
                                {hasDriving && (
                                    <div className="mt-3 space-y-3" data-testid="reg-driving-fields">
                                        <Field icon={Car} testid="reg-license-number" placeholder="Driver's licence number" value={licenseNumber} onChange={setLicenseNumber} />
                                        <Field icon={ShieldCheck} testid="reg-insurance-policy" placeholder="Vehicle insurance policy number" value={insurancePolicy} onChange={setInsurancePolicy} />
                                        <p className="text-[11px] leading-relaxed text-zinc-400">
                                            We'll perform verification on these details. If you pass, you'll be eligible for jobs that involve driving.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        <div onFocusCapture={() => guideSpeak("Now make up a password you'll remember — then tap the big orange Create Account button. All done!")}>
                            <PasswordField testid="reg-password" placeholder="Create a password" value={regPw} onChange={setRegPw} show={showPw} setShow={setShowPw} />
                        </div>
                        <div>
                            <Field icon={Gift} testid="reg-referral-code" placeholder="Referral code (optional)" value={refCode} onChange={(v) => setRefCode(v.toUpperCase())} />
                            {refCode && <p data-testid="reg-referral-hint" className="mt-1 text-[11px] text-amber-300/90">Referred by a friend — welcome aboard! 🎁</p>}
                        </div>
                        <SubmitButton testid="signup-submit" loading={loading} label="Create Account" />
                    </motion.form>
                )}
            </AnimatePresence>

            {googleCfg && (
                <div data-testid="google-auth-section" className="mt-5">
                    <div className="flex items-center gap-3">
                        <span className="h-px flex-1 bg-white/10" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">or</span>
                        <span className="h-px flex-1 bg-white/10" />
                    </div>
                    <div ref={gbtnRef} className="mt-4 flex min-h-[44px] justify-center overflow-hidden">
                        {googleButton}
                    </div>
                </div>
            )}

            <p className="mt-6 text-center text-[11px] text-zinc-400 leading-relaxed">
                By continuing you agree to FORCE&apos;s{" "}
                <Link to="/terms" data-testid="auth-terms-link" className="text-orange-300 underline-offset-2 hover:underline">Terms</Link>
                {" "}&amp;{" "}
                <Link to="/privacy" data-testid="auth-privacy-link" className="text-orange-300 underline-offset-2 hover:underline">Privacy</Link>.
                {" "}Your session stays signed in on this device.
            </p>

            {/* Instant licence verification result (driving jobs) */}
            {verify && (
                <div data-testid="driving-verify-modal" className="fixed inset-0 z-[130] flex items-center justify-center p-5">
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
                    <motion.div
                        initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="glass relative z-10 w-full max-w-md p-7 text-center"
                    >
                        <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-orange-500/15 border border-orange-500/40">
                            <Car className="h-7 w-7 text-orange-400" />
                        </div>
                        <h3 className="mt-3 font-display text-2xl font-black uppercase tracking-tight text-white">Licence check</h3>
                        {verify.status === "validating" ? (
                            <div className="mt-4">
                                <p className="text-sm text-zinc-300">Validating your driver's licence with the Ontario database…</p>
                                <div data-testid="driving-verify-progress" className="mt-5 h-2.5 w-full overflow-hidden rounded-full bg-white/10">
                                    <motion.div
                                        className="h-full w-2/5 rounded-full bg-gradient-to-r from-orange-500 to-amber-400"
                                        initial={{ x: "-110%" }}
                                        animate={{ x: "280%" }}
                                        transition={{ repeat: Infinity, duration: 1.15, ease: "easeInOut" }}
                                    />
                                </div>
                                <p className="mt-3 text-[11px] text-zinc-500">Usually takes a few seconds — don't close this.</p>
                            </div>
                        ) : (
                            <div className="mt-4" data-testid="driving-verify-result" data-status={verify.status}>
                                <div className="flex justify-center">
                                    {verify.status === "passed" && <CheckCircle2 className="h-12 w-12 text-emerald-400" />}
                                    {verify.status === "failed" && <XCircle className="h-12 w-12 text-red-400" />}
                                    {verify.status === "pending" && <Clock3 className="h-12 w-12 text-amber-400" />}
                                </div>
                                <p className={`mt-2 font-display text-xl font-extrabold ${verify.status === "passed" ? "text-emerald-300" : verify.status === "failed" ? "text-red-300" : "text-amber-300"}`}>
                                    {verify.status === "passed" ? "Verification passed!" : verify.status === "failed" ? "Verification didn't pass" : "Under review"}
                                </p>
                                <p className="mt-2 text-sm leading-relaxed text-zinc-300">{verify.message}</p>
                                {verify.status === "passed" && (
                                    <p className="mt-2 text-xs font-semibold text-emerald-400/90">You can now apply to jobs that involve driving.</p>
                                )}
                                <button data-testid="driving-verify-continue" onClick={finishVerification} className="mt-5 w-full rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 py-3 font-bold text-zinc-950 hover:brightness-110 transition-[filter]">
                                    Continue
                                </button>
                            </div>
                        )}
                    </motion.div>
                </div>
            )}

            {rolePick && (
                <div data-testid="role-choice-modal" className="fixed inset-0 z-[130] flex items-center justify-center p-5">
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
                    <motion.div
                        initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="glass relative z-10 w-full max-w-md p-7 text-center"
                    >
                        <h3 className="font-display text-2xl font-black uppercase tracking-tight text-white">Welcome to FORCE</h3>
                        <p className="mt-2 text-sm text-zinc-400">One quick thing — how will you use FORCE?</p>
                        <div className="mt-6 grid grid-cols-3 gap-2.5">
                            <RoleCard testid="role-choice-homeowner" active={false} onClick={() => chooseRole("homeowner")} icon={Home} title="I need help" sub="Hire a pro" />
                            <RoleCard testid="role-choice-worker" active={false} onClick={() => chooseRole("worker")} icon={Hammer} title="I want work" sub="Find local gigs" />
                            <RoleCard testid="role-choice-business" active={false} onClick={() => chooseRole("business")} icon={Store} title="I'm a business" sub="Advertise here" />
                        </div>
                        <p className="mt-4 text-[11px] text-zinc-500">You can switch anytime in Settings.</p>
                    </motion.div>
                </div>
            )}
        </motion.div>
    );
}

function computeAge(birthday) {
    if (!birthday) return null;
    const d = new Date(birthday);
    if (isNaN(d.getTime())) return null;
    const t = new Date();
    let age = t.getFullYear() - d.getFullYear();
    const m = t.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && t.getDate() < d.getDate())) age--;
    return age;
}

function Field({ icon: Icon, testid, type = "text", placeholder, value, onChange }) {
    return (
        <div className="relative">
            <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-orange-400/80" />
            <input
                data-testid={testid}
                type={type}
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="field pl-10"
            />
        </div>
    );
}

function BirthdayField({ testid, value, onChange, age }) {
    const today = new Date().toISOString().split("T")[0];
    return (
        <div className="relative">
            <CalendarClock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-orange-400/80" />
            <input
                data-testid={testid}
                type="date"
                max={today}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                aria-label="Birthday"
                className={`field pl-10 pr-12 [color-scheme:dark] ${value ? "text-white" : "text-zinc-500"}`}
            />
            {age !== null && age >= 0 && (
                <span
                    data-testid="reg-age-preview"
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-semibold text-orange-300"
                >
                    {age} yrs
                </span>
            )}
        </div>
    );
}

function PasswordField({ testid, placeholder = "Password", value, onChange, show, setShow }) {
    return (
        <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-orange-400/80" />
            <input
                data-testid={testid}
                type={show ? "text" : "password"}
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="field pl-10 pr-10"
            />
            <button
                type="button"
                onClick={() => setShow(!show)}
                data-testid="toggle-password"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-orange-300"
            >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
        </div>
    );
}

function RoleCard({ testid, active, onClick, icon: Icon, title, sub }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            className={`relative rounded-2xl p-3.5 text-left border transition-[transform,border-color,background-color] duration-300 ${
                active
                    ? "border-orange-500/60 bg-orange-500/10 shadow-[0_0_20px_rgba(255,122,0,0.18)]"
                    : "border-white/10 bg-zinc-950/50 hover:border-white/25"
            }`}
        >
            <Icon className={`h-5 w-5 mb-2 ${active ? "text-orange-400" : "text-zinc-500"}`} />
            <div className="text-sm font-bold text-white">{title}</div>
            <div className="text-[11px] text-zinc-400">{sub}</div>
        </button>
    );
}

function SubmitButton({ testid, loading, label }) {
    return (
        <button
            data-testid={testid}
            disabled={loading}
            className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-orange-500 via-[#FF7A00] to-amber-500 py-3.5 font-bold text-zinc-950 shadow-[0_8px_30px_rgba(255,122,0,0.38)] transition-[box-shadow,filter,transform] duration-300 hover:shadow-[0_8px_44px_rgba(255,122,0,0.6)] hover:brightness-110 active:scale-[0.99] disabled:opacity-60"
        >
            <span className="relative z-10 flex items-center justify-center gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {label}
                {!loading && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
            </span>
        </button>
    );
}
